import { FOLDER_ROOT_PATH } from '@lib/library/folderPath';
import { Skeleton } from '@mui/material';
import {
  LibraryDeleteModal,
  LibraryDeleteModalRef,
} from '@pages/Libraries/components/LibraryDeleteModal';
import { LibraryMenuButton } from '@pages/Libraries/components/LibraryMenuButton';
import {
  LibraryRenameModal,
  LibraryRenameModalRef,
} from '@pages/Libraries/components/LibraryRenameModal';
import { ReadOnlyLibraryIndicator } from '@pages/Libraries/components/ReadOnlyLibraryIndicator';
import { TranscriptOnlyIndicator } from '@pages/Libraries/components/TranscriptOnlyIndicator';
import { FolderMenuButton } from '@pages/Library/components/Folders/FolderMenuButton';
import { LibraryTitleTrail } from '@pages/Library/components/Folders/LibraryTitleTrail';
import { LibraryActionRow } from '@pages/Library/components/LibraryActionRow/LibraryActionRow';
import { useLibraryUploadingCount } from '@pages/Library/components/LibraryToolbar/LibraryStats';
import { getUploadHeaderLabel } from '@pages/Library/utils/uploadCalculations';
import { useLibrary } from '@stores/Library/Library';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { observer } from 'mobx-react';
import { useRef } from 'react';
import css from './LibraryContentHeaderLeft.module.scss';

interface LibraryContentHeaderLeftProps {
  /**
   * Abbreviate the two verb buttons to a bare icon rail. Decided by the header from a measurement
   * of the WHOLE row (`VERB_LABELS_BELOW` in `LibraryContent`), not from this cluster's own width —
   * see the note there: the title row these pills sit in gets wider when the RIGHT cluster
   * abbreviates, so a threshold measured here reappeared the labels on a narrower window.
   */
  verbsCollapsed?: boolean;
}

export const LibraryContentHeaderLeft = observer(
  ({ verbsCollapsed = false }: LibraryContentHeaderLeftProps) => {
    const library = useLibrary();
    const uploads = useUploadManager();
    const folder = useLibraryFolder();
    const inFolder = folder.currentFolderPath !== FOLDER_ROOT_PATH;
    const loaded = library.name !== undefined;

    const renameModalRef = useRef<LibraryRenameModalRef | null>(null);
    const deleteModalRef = useRef<LibraryDeleteModalRef | null>(null);
    const disableDelete = library.isDefault || library.isDemo || library.isReadOnly;
    const isReadOnly = library.isReadOnly;

    const uploadingCount = useLibraryUploadingCount();
    const backendProcessingCount = folder.filesIndexing.length + folder.filesQueued.length;

    return (
      <div className={css.headerLeft}>
        <span className={css.headerLeftTitle}>
          {/* Looks like the bare-text-conditional crash described in `LibraryStats`, and has twice been
            flagged as it, but is safe — do not "fix" it by wrapping the name in a span.
            A lone string child takes react-dom's `shouldSetTextContent` path: `nextChildren`
            becomes null, no text fiber is created, and the name is written straight to
            `textContent`. There is no node for `removeChild` to fail on. When `loaded` flips
            back to false React flags `ContentReset` and the commit does `textContent = ''`,
            which clears any `<font>` a translator inserted along with everything else.
            The `•` below DID crash because it had element siblings, so it got its own
            text fiber that React removes individually. */}
          {inFolder ? (
            <LibraryTitleTrail path={folder.currentFolderPath} />
          ) : (
            <h2 title={library.name}>
              {loaded ? library.name : <Skeleton variant="text" height="32px" width="20ch" />}
            </h2>
          )}
          {loaded && library.isTranscriptOnly && (
            <TranscriptOnlyIndicator className={css.headerTranscriptIcon} />
          )}
          {/* Inside a folder the ⋯ is the folder's: the title names the folder, and the library's
            own menu is one click away on its name in the trail. */}
          {isReadOnly ? (
            <ReadOnlyLibraryIndicator />
          ) : inFolder ? (
            <FolderMenuButton path={folder.currentFolderPath} className={css.folderMenu} />
          ) : (
            <LibraryMenuButton
              onClick={() => {}} // optional: keep if you want to hook selection later
              disableDelete={disableDelete}
              renameModalRef={renameModalRef}
              deleteModalRef={deleteModalRef}
              variant="in-library"
              libraryId={library.libraryId}
            />
          )}
          {/* Inline beside the name rather than on its own row below the header:
            these are things you do with this library, so they belong in the
            library's own control cluster. The row carries its own left margin to
            keep the two clusters visibly apart. */}
          <LibraryActionRow collapsed={verbsCollapsed} />
        </span>
        {/* Live counters, so out of reach of the page translator. See `LibraryStats`. */}
        {uploadingCount > 0 && loaded && (
          <span className={css.processingLine} translate="no">
            {getUploadHeaderLabel(uploads.globalUploadEta, uploadingCount)}
          </span>
        )}
        {uploadingCount === 0 && backendProcessingCount > 0 && loaded && (
          <span className={css.processingLine} translate="no">
            Processing {backendProcessingCount} media, you may close this tab.
          </span>
        )}
        {/* Modals */}
        {!isReadOnly && (
          <>
            <LibraryRenameModal
              ref={renameModalRef}
              libraryId={library.libraryId}
              currentName={library.name || ''}
            />
            <LibraryDeleteModal
              ref={deleteModalRef}
              libraryId={library.libraryId}
              currentName={library.name || ''}
            />
          </>
        )}
      </div>
    );
  },
);
